import { Injectable, OnDestroy } from '@angular/core';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { RequestsProvider } from '../requests/requests';
import { StorageProvider } from '../storage/storage';
import { NetworkProvider, NetworkStatus } from '../network/network';
import { LoggerFactory } from '../logger/logger-factory.service';
import { environment } from '../../../../environments/environment';
import { deepCopy } from '../../utils/deepCopy';
import { Subscription } from 'rxjs';
import { PostCrRequest } from '../../models/v2/post-cr.model';

export type UploadPreSignedRequest = {
  id_document?: string;
  id_ot?: string;
  type?: string;
  taille?: number;
  nom?: string;
  tags?: TagRequest[];
  contenu?: string;
  mime_type?: string;
};
export type TagRequest = {
  nom_tag: string;
  valeur_tag: string;
};
export type UploadPreSignedResponse = {
  id_document?: string;
  url?: string;
};
@Injectable({
  providedIn: 'root',
})
export class PostPhotoService implements OnDestroy {
  presignedApiUrl = '';
  offline: boolean = false;

  private logger: LoggerWrapper;
  private networkConnected: Subscription;

  constructor(
    private requestsProvider: RequestsProvider,
    private storageProvider: StorageProvider,
    private networkProvider: NetworkProvider,
    private loggerFactory: LoggerFactory
  ) {
    this.logger = this.loggerFactory.getInstance(this, true);
    this.presignedApiUrl = environment.apis.API_DACTECH_BACK_V1 + '/presigned/upload';
    this.networkConnected = this.networkProvider.networkStatusSubject.subscribe((status) => {
      this.offline = status !== NetworkStatus.DEGRADED && status !== NetworkStatus.ONLINE;
    });
  }

  ngOnDestroy(): void {
    if (this.networkConnected) {
      this.networkConnected.unsubscribe();
    }
  }

  async retryPostPhotoForOt(postCr: PostCrRequest) {
    let ids_ots: string[] = postCr.gammeFilles?.map((gamme) => gamme.id_ot) || [];
    ids_ots.push(postCr.ot.id_ot);

    let photoEnvoyees = 0;
    for (let idOt of ids_ots) {
      let pjsToSend: UploadPreSignedRequest[] = (await this.storageProvider.get(this.getKeyPhotoOt(idOt), false)) || [];
      if (pjsToSend && pjsToSend.length > 0) {
        this.logger.info(
          "Renvoi des photos pour l'OT " + idOt + ', Nombre de photos restant à envoyer:' + pjsToSend.length
        );
        for (let pj of pjsToSend) {
          if (!this.offline) {
            this.logger.debug('Renvoie de la photo ', pj.id_document);
            if (await this.postOnline(pj)) {
              photoEnvoyees++;
            }
          }
        }
      }
      this.logger.info("Renvoi des photos pour l'OT " + idOt + ', Nombre de photos envoyees:' + photoEnvoyees);
      await this.storageProvider.remove(this.getKeyPhotoOt(idOt));
    }
  }

  postPhoto(idDocument: string, idOt: string, idOperation: string, nom: string, contenu: string, mimetype: string) {
    return new Promise(() => {
      let tags: TagRequest[] = [];
      tags.push({
        nom_tag: 'id_ot',
        valeur_tag: idOt,
      });
      tags.push({
        nom_tag: 'id_operation',
        valeur_tag: idOperation,
      });
      let request: UploadPreSignedRequest = {
        id_document: idDocument,
        id_ot: idOt,
        taille: contenu.length,
        type: 'photo',
        contenu: contenu,
        nom: nom,
        mime_type: mimetype,
        tags: tags,
      };
      if (!this.offline) {
        this.postOnline(request).then();
      } else {
        this.addRequestToStorage(request).then();
      }
    });
  }

  private async postOnline(request: UploadPreSignedRequest) {
    try {
      let toSend = deepCopy(request);
      toSend.contenu = null;
      this.logger.debug("Demande de l'url presigne", toSend);
      let response: UploadPreSignedResponse = await this.requestsProvider.post(this.presignedApiUrl, toSend);
      let blob = await fetch('data:image/jpeg;base64,' + request.contenu).then((res) => res.blob());
      let fileToUpload = new File([blob], request.nom);
      this.logger.debug("Envoi de la pj à l'url presigne", toSend);
      await this.requestsProvider.put(response.url, fileToUpload, { 'Content-Type': request.mime_type });
      await this.removeRequestFromStorage(request.id_ot, request.id_document, request.nom);
      return true;
    } catch (error) {
      await this.addRequestToStorage(request, error);
      return false;
    }
  }

  public async removeRequestFromStorage(id_ot: string, id_document: string, nom: string): Promise<void> {
    let data: UploadPreSignedRequest[] = (await this.storageProvider.get(this.getKeyPhotoOt(id_ot), false)) || [];
    data = data.filter((item: UploadPreSignedRequest) => item.id_document !== id_document);
    if (data.length > 0) {
      await this.storageProvider.set(this.getKeyPhotoOt(id_ot), data, false);
    } else {
      await this.storageProvider.remove(this.getKeyPhotoOt(id_ot));
    }
  }

  private async addRequestToStorage(request: UploadPreSignedRequest, error = null) {
    if (error) {
      this.logger.error(error, "Erreur d'envoi de la pj à l'url presigne", request);
    }
    let data = (await this.storageProvider.get(this.getKeyPhotoOt(request.id_ot), false)) || [];
    data.push(request);
    await this.storageProvider.set(this.getKeyPhotoOt(request.id_ot), data, false);
  }

  private getKeyPhotoOt(id_ot: string): string {
    return 'unsend_photo_ot_' + id_ot;
  }
}
